<template>
  <div class="transfer-destination">
    <el-radio-group v-model="kind" size="mini" :disabled="disabled" @change="onKind">
      <el-radio-button label="OPERATOR">{{ text.mappingDestOperator }}</el-radio-button>
      <el-radio-button label="AGENCY">{{ text.mappingDestAgency }}</el-radio-button>
    </el-radio-group>

    <template v-if="kind === 'OPERATOR'">
      <el-select
        v-model="operatorDirect"
        class="transfer-destination__direct"
        size="small"
        filterable
        clearable
        :disabled="disabled"
        :placeholder="text.transferDestPlaceholder"
        @change="onDirect">
        <el-option v-for="row in directOptions" :key="row.key" :label="row.label" :value="row.key"></el-option>
      </el-select>
      <p v-if="directOptions.length === 0" class="transfer-destination__hint">{{ text.transferNoDestination }}</p>
    </template>

    <template v-else>
      <!-- 관리점은 자기 운영사 안에서만 고른다 — 다른 운영사의 관리점을 고르면 서버가 400 이다(§3-6) -->
      <el-select
        v-model="operatorCd"
        class="transfer-destination__operator"
        size="small"
        filterable
        clearable
        :disabled="disabled"
        :placeholder="text.operatorLabel"
        @change="onOperator">
        <el-option v-for="row in activeOperators" :key="row.id" :label="row.label" :value="row.operatorCd"></el-option>
      </el-select>
      <AgencyPicker
        v-model="agencyId"
        class="transfer-destination__agency"
        :disabled="disabled || operatorCd == null"
        :operator-cd="operatorCd"
        :exclude-id="excludeAgencyCd"
        :placeholder="text.agencyNmLabel"
        @change="onAgency" />
    </template>
  </div>
</template>

<script>
import AgencyPicker from '@/components/callpop/AgencyPicker.vue'
import { operatorDestinations, operatorOptionsOf } from '@/utils/callpopOrg'
import { CALLPOP_TEXT } from '@/utils/callpopText'

/**
 * **이관·매핑 목적지 선택기 한 곳** — 매장 매핑 다이얼로그와 관리점 비활성 이관이 이것을 공유한다.
 *
 * 목적지는 세 갈래지만 축은 둘이다: 운영사 **직할**(총괄사면 「총괄사 직할」로 읽힌다)과 **관리점**이다.
 * 직할 후보는 순수 함수([operatorDestinations])가 만들고 현재 위치를 뺀다. 관리점은 후보가 수백이 될 수
 * 있어 목록으로 세우지 않고 원격 검색(`AgencyPicker`)이 답한다.
 *
 * **자격 판정은 하지 않는다** — 「이 사람이 그 목적지로 옮길 수 있는가」는 서버 `OrgTransferPolicy` 가
 * 정본이고, 화면은 403 문구를 그대로 보인다. 여기서 거르는 것은 **데이터 규칙**(비활성 운영사·현재 위치)뿐이다.
 */
export default {
  name: 'TransferDestinationPicker',

  components: { AgencyPicker },

  props: {
    /** `{operatorCd, agencyCd}` 또는 `null`(`v-model`) */
    value: { type: Object, default: null },
    /** `GET /callpop/operators` 의 행 */
    operators: { type: Array, default: () => [] },
    /** 현재 매핑 — 그 자리는 목적지가 아니다(`{operator, agency}`) */
    current: { type: Object, default: null },
    /**
     * 목적지에서 뺄 관리점 — **자기 자신**이다(비활성하려는 그 관리점).
     *
     * 소속 매장을 옮겨야 비활성할 수 있는데 옮길 곳으로 자기가 보이면, 고르는 순간 아무것도 옮기지
     * 않은 채 서버가 거절한다. 고를 수 없는 것을 보이지 않는 편이 낫다.
     */
    excludeAgencyCd: { type: [Number, String], default: null },
    disabled: { type: Boolean, default: false },
    /** 다이얼로그를 다시 열 때마다 올린다 — 고르던 축·값을 처음 상태로 되돌린다 */
    resetSeq: { type: Number, default: 0 },
  },

  data() {
    return {
      text: CALLPOP_TEXT,
      kind: 'OPERATOR',
      operatorDirect: '',
      operatorCd: null,
      agencyId: '',
    }
  },

  computed: {
    directOptions() {
      return operatorDestinations(this.operators, this.current)
    },
    activeOperators() {
      return operatorOptionsOf(this.operators).filter((row) => row.useYn !== 'N')
    },
  },

  watch: {
    /**
     * **새 세션**이다 — 다이얼로그가 다시 열렸으니 고르던 축·값을 처음 상태로 되돌린다.
     *
     * 종전에는 「`value` 가 비었다」로 그것을 알아보려 했는데, 값이 비는 경우는 둘이다: 새로 열렸거나,
     * **고르는 중이라 아직 목적지가 완성되지 않았다**(운영사만 고른 상태). 뒤엣것까지 초기화하는 바람에
     * 방금 고른 운영사가 사라졌다. 「새로 열렸다」는 부모만 아는 사실이라 부모가 말한다.
     */
    resetSeq() {
      this.kind = 'OPERATOR'
      this.operatorDirect = ''
      this.operatorCd = null
      this.agencyId = ''
    },
  },

  methods: {
    onKind() {
      this.operatorDirect = ''
      this.operatorCd = null
      this.agencyId = ''
      this.$emit('input', null)
    },

    onDirect(key) {
      const found = this.directOptions.find((row) => row.key === key) || null

      this.$emit('input', found ? { operatorCd: found.operatorCd, agencyCd: null } : null)
    },

    onOperator() {
      // 운영사가 바뀌면 앞 운영사의 관리점은 고를 수 있는 것이 아니다
      this.agencyId = ''
      this.$emit('input', null)
    },

    onAgency(id, option) {
      if (!id || !option) {
        this.$emit('input', null)

        return
      }

      this.$emit('input', {
        operatorCd: option.operatorCd != null ? option.operatorCd : this.operatorCd,
        agencyCd: option.agencyCd,
      })
    },
  },
}
</script>

<style scoped>
.transfer-destination__direct,
.transfer-destination__operator,
.transfer-destination__agency {
  display: block;
  margin-top: 8px;
  width: 100%;
}
.transfer-destination__hint {
  color: #e6a23c;
  font-size: 12px;
  margin: 6px 0 0;
}
</style>
